import { format } from 'ol/coordinate';

export interface ContentMetadata {
  documentType: string;
  data: any;
  createdAt: string;
  updatedAt?: string;
  version: number;
}

export interface DocumentResponse {
  id: string;
  documentId?: string;
  fileName: string;
  type: string;
  contentUri: string;
  createdAt: string;
  updatedAt?: string;
  version: number;
  contentMetadata?: ContentMetadata;
  ecmDocId?: number;
  extractionStatus?: string;
}

export class TranscriptMetaDataSdk {
  private baseUrl: string;

  /**
   * Constructor
   * @param baseURL the base URL for the service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseUrl = baseURL;
  }

  /**
   * Wrapper for fetch requests with error handling
   * @param endpoint The API endpoint
   * @param options Fetch options
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.message}`);
    }

    return response;
  }

  /**
   * Save extraction data for a transcript
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   * @param data The extraction data to save
   */
  async saveExtraction(accessToken: string, transId: string, data: any): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(r => r.json());
  }

  /**
   * Get all transcript documents
   * @param accessToken The authentication token
   */
  async getAllDocuments(accessToken: string): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` }
    })
      .then(r => r.json())
      .then(data => {
        // Handle both array response and wrapped response
        if (Array.isArray(data)) {
          return data;
        }
        if (data.transcripts && Array.isArray(data.transcripts)) {
          return data.transcripts;
        }
        return [];
      });
  }

  /**
   * Load extraction data for a specific transcript
   * @param accessToken The authentication token
   * @param transcriptId The transcript identifier
   */
  async loadExtraction(accessToken: string, transcriptId: string): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transcriptId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` }
    }).then(r => {
      if (!r.ok) {
        throw new Error(`Load extraction failed with status: ${r.status}`);
      }
      return r.json();
    });
  }

  /**
   * Delete a transcript document
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   */
  async deleteDocument(accessToken: string, transId: string): Promise<void> {
    await fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` }
    });
  }

  /**
   * Save the status of a transcript
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   * @param statusId The status identifier
   */
  async saveStatus(accessToken: string, transId: string, statusId: number): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status_id: statusId })
    });
  }

  /**
   * Create transcript metadata with a document_id
   * @param accessToken The authentication token
   * @param tenantId The tenant identifier
   * @param documentId The document identifier from presigned URL response
   * @param documentSource The source of the document
   * @param fileName The name of the file being processed
   * @param typeId The transcript type being sent
   * @returns Promise with transcript metadata including trans_id
   */
  async createTranscriptMetadata(
    accessToken: string,
    tenantId: string,
    documentId: string,
    documentSource?: string,
    fileName?: string,
    typeId?: number | null
  ): Promise<any> {
    try {
      const postBody = {
        document_id: documentId,
        document_source: documentSource || 'transcriptProcessing',
        ...(fileName && { file_name: fileName }),
        ...(typeId !== null && typeId !== undefined && { type_id: typeId })
      };
      const jsonBody = JSON.stringify(postBody);
      const requestHeaders = new Headers({
        'Content-Type': 'application/json',
        tenantId: `${tenantId}`,
        Authorization: `Bearer ${accessToken}`
      });

      return fetch(`${this.baseUrl}/v1/transcript/metadata`, {
        method: 'POST',
        headers: requestHeaders,
        body: jsonBody
      }).then(response => {
        if (!response.ok) {
          throw new Error(`Create transcript metadata failed with status: ${response.status}`);
        }
        return response.json();
      });
    } catch (error) {
      console.error('Create transcript metadata failed:', error);
      throw error;
    }
  }

  /**
   * Lock a transcript for editing
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   * @param userDisplayName The display name of the user
   */
  async lockTranscript(
    accessToken: string,
    transId: string,
    userDisplayName: string
  ): Promise<void> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}/assign`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ user_display_name: userDisplayName })
    }).then(r => {
      if (!r.ok) {
        throw new Error(`Assign failed with status: ${r.status}`);
      }
    });
  }

  /**
   * Unlock a transcript
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   */
  async unlockTranscript(accessToken: string, transId: string): Promise<void> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}/unassign`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    }).then(r => {
      if (!r.ok) {
        throw new Error(`Unassign failed with status: ${r.status}`);
      }
    });
  }

  /**
   * Get the status of a transcript
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   */
  async getTranscriptStatus(accessToken: string, transId: string): Promise<number> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/status/${transId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }).then(r => {
      if (!r.ok) {
        throw new Error(`Get status failed with status: ${r.status}`);
      }
      return r.json();
    });
  }

  /**
   * Get the content of a transcript
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   */
  async getTranscriptContent(accessToken: string, transId: string): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }).then(r => {
      if (!r.ok) {
        throw new Error(`Get transcript content failed with status: ${r.status}`);
      }
      return r.json();
    });
  }

  /**
   * Get the metadata summary of a transcript
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   */
  async getTranscriptMetadataSummary(accessToken: string, transId: string): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }).then(r => {
      if (!r.ok) {
        throw new Error(`Get transcript summary failed with status: ${r.status}`);
      }
      return r.json();
    });
  }

  /**
   * Record a download audit event for a transcript
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   */
  async transcriptDownloadAudit(accessToken: string, transId: string): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}/download-audit`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    }).then(r => r.json());
  }

  /**
   * Record a view audit event for a transcript
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   */
  async transcriptViewAudit(accessToken: string, transId: string): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}/view-audit`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    }).then(r => r.json());
  }

  /**
   * Change a transcript's type, marking it as a user-selected override and
   * re-enqueuing extraction with the new type as authoritative.
   * @param accessToken The authentication token
   * @param transId The transcript identifier
   * @param typeId The new transcript type
   */
  async changeTranscriptType(accessToken: string, transId: string, typeId: number): Promise<any> {
    return fetch(`${this.baseUrl}/v1/transcript/metadata/${transId}/change-type`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ type_id: typeId })
    }).then(r => {
      if (!r.ok) {
        throw new Error(`Change transcript type failed with status: ${r.status}`);
      }
      return r.json();
    });
  }

  /**
   * Enqueue a transcript for processing
   * @param accessToken The authentication token
   * @param s3Key The S3 key/path of the uploaded transcript file
   * @param bucket The S3 bucket where the file is stored
   * @param tenantId The tenant identifier
   * @param transcriptMetadataId The transcript metadata record ID
   */
  async enqueueTranscript(
    accessToken: string,
    s3Key: string,
    bucket: string,
    tenantId: string,
    transcriptMetadataId: string
  ): Promise<any> {
    const params = new URLSearchParams({
      s3_key: s3Key,
      bucket: bucket,
      tenant_id: tenantId,
      transcript_metadata_id: transcriptMetadataId
    });
    return fetch(`${this.baseUrl}/v2/transcript/enqueue?${params.toString()}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    }).then(r => {
      if (!r.ok) {
        throw new Error(`Enqueue transcript failed with status: ${r.status}`);
      }
      return r.json();
    });
  }
}

export default TranscriptMetaDataSdk;
